import React from "react";
import "../DesignerPropertiesPanel.css";

const numberOrZero = (value) => {
  const n = Number(value);
  return Number.isFinite(n) ? n : 0;
};

const normalizeColorValue = (value, fallback = "#000000") => {
  if (!value || typeof value !== "string") return fallback;

  const trimmed = value.trim();
  const hexColorRegex = /^#([0-9A-Fa-f]{3}|[0-9A-Fa-f]{6})$/;

  if (hexColorRegex.test(trimmed)) return trimmed;
  return fallback;
};

const ColorField = ({ label, value, onTextChange, onPickerChange, fallback }) => {
  const pickerValue = normalizeColorValue(value, fallback);

  return (
    <div className="dp-field">
      <label className="dp-label">{label}</label>

      <div className="dp-color-row">
        <input
          type="text"
          value={value || ""}
          onChange={(e) => onTextChange(e.target.value)}
          placeholder={fallback}
          className="dp-input"
        />

        <input
          type="color"
          value={pickerValue}
          onChange={(e) => onPickerChange(e.target.value)}
          className="dp-color-input"
        />
      </div>
    </div>
  );
};

const shapeOptions = [
  { value: "rectangle", label: "Rect", icon: "▭" },
  { value: "square", label: "Square", icon: "□" },
  { value: "circle", label: "Circle", icon: "◯" },
  { value: "ellipse", label: "Ellipse", icon: "⬭" },
  { value: "triangle", label: "Triangle", icon: "△" },
  { value: "line", label: "Line", icon: "／" },
  { value: "polygon", label: "Polygon", icon: "⬡" },
  { value: "custom", label: "Custom", icon: "〽" },
];

const ShapeProperties = ({
  selectedObject,
  updateObjectDataField,
  updateObjectStyleField,
}) => {
  const dataJson = selectedObject.data_json || {};
  const styleJson = selectedObject.style_json || {};
  const shapeType = dataJson.shape_type || "rectangle";

  return (
    <div className="dp-section">
      <h4 className="dp-section-title">Shape</h4>

      <div className="dp-field">
        <label className="dp-label">Shape Type</label>

        <div className="dp-shape-picker">
          {shapeOptions.map((shape) => (
            <button
              key={shape.value}
              type="button"
              className={`dp-shape-option ${
                shapeType === shape.value ? "dp-shape-option--active" : ""
              }`}
              onClick={() =>
                updateObjectDataField(selectedObject.id, "shape_type", shape.value)
              }
            >
              <div style={{ fontSize: 28, lineHeight: 1 }}>{shape.icon}</div>
              <div className="dp-shape-option-label">{shape.label}</div>
            </button>
          ))}
        </div>
      </div>

      {shapeType === "polygon" && (
        <div className="dp-field">
          <label className="dp-label">Polygon Sides</label>
          <input
            type="number"
            min="5"
            value={dataJson.polygon_sides ?? 6}
            onChange={(e) =>
              updateObjectDataField(
                selectedObject.id,
                "polygon_sides",
                Math.max(5, numberOrZero(e.target.value))
              )
            }
            className="dp-input"
          />
        </div>
      )}

      {(shapeType === "custom" || shapeType === "polygon" || shapeType === "triangle") && (
        <div className="dp-field">
          <label className="dp-label">Render Mode</label>
          <select
            value={dataJson.render_mode || "filled"}
            onChange={(e) =>
              updateObjectDataField(selectedObject.id, "render_mode", e.target.value)
            }
            className="dp-select"
          >
            <option value="filled">Filled</option>
            <option value="outline">Outline</option>
          </select>
        </div>
      )}

      <div className="dp-field">
        <label className="dp-checkbox-label">
          <input
            type="checkbox"
            checked={dataJson.transparent_fill ?? false}
            onChange={(e) =>
              updateObjectDataField(
                selectedObject.id,
                "transparent_fill",
                e.target.checked
              )
            }
          />
          Transparent Fill
        </label>
      </div>

      <ColorField
        label="Fill Color"
        value={styleJson.backgroundColor || ""}
        fallback="#93c5fd"
        onTextChange={(value) =>
          updateObjectStyleField(selectedObject.id, "backgroundColor", value)
        }
        onPickerChange={(value) =>
          updateObjectStyleField(selectedObject.id, "backgroundColor", value)
        }
      />

      <ColorField
        label="Border Color"
        value={styleJson.borderColor || ""}
        fallback="#2563eb"
        onTextChange={(value) =>
          updateObjectStyleField(selectedObject.id, "borderColor", value)
        }
        onPickerChange={(value) =>
          updateObjectStyleField(selectedObject.id, "borderColor", value)
        }
      />

      <div className="dp-grid-2">
        <div className="dp-field">
          <label className="dp-label">Border Width</label>
          <input
            type="number"
            min="0"
            value={styleJson.borderWidth ?? 2}
            onChange={(e) =>
              updateObjectStyleField(
                selectedObject.id,
                "borderWidth",
                Math.max(0, numberOrZero(e.target.value))
              )
            }
            className="dp-input"
          />
        </div>

        <div className="dp-field">
          <label className="dp-label">Border Radius</label>
          <input
            type="number"
            min="0"
            value={styleJson.borderRadius ?? 8}
            onChange={(e) =>
              updateObjectStyleField(
                selectedObject.id,
                "borderRadius",
                Math.max(0, numberOrZero(e.target.value))
              )
            }
            className="dp-input"
          />
        </div>
      </div>

      {shapeType === "line" && (
        <div className="dp-field">
          <label className="dp-label">Line Thickness</label>
          <input
            type="number"
            min="1"
            value={styleJson.lineThickness ?? styleJson.borderWidth ?? 2}
            onChange={(e) =>
              updateObjectStyleField(
                selectedObject.id,
                "lineThickness",
                Math.max(1, numberOrZero(e.target.value))
              )
            }
            className="dp-input"
          />
        </div>
      )}
    </div>
  );
};

export default ShapeProperties;